import { deserialize, type CborMap } from '@mesh/friendly-cbor-serializer'
import { MeshAgent } from '../agent'
import { portalHost } from '../environment'
import {
  MeshAppMessage,
  MeshAppNavigationMessage,
  MeshAppRequestMessage,
  navigationMessageKind,
  notificationMessageKind,
  requestMessageKind,
} from './message'
import { ResponseHeaderSchema } from '../agent/messages/responseHeader'
import AgentStatusCodes from '../agent/agentStatusCodes'
import { LookupSiteResponsePayload } from '../agent/messages'
import AgentMessageTypes from '../agent/agentMessageTypes'
import { Id } from '../aliases'

export class MeshAppBridge {
  private appHost?: HTMLIFrameElement
  private meshAgent?: MeshAgent
  private boundHandleMessage: (event: MessageEvent) => void
  private initialData?: LookupSiteResponsePayload

  constructor(meshAgent: MeshAgent) {
    this.meshAgent = meshAgent
    this.boundHandleMessage = this.eventListener.bind(this)
    window.addEventListener('message', this.boundHandleMessage)
    console.log('MeshAppBridge initialized')
  }

  public setAppHost(appHost: HTMLIFrameElement) {
    this.appHost = appHost
  }

  private postInitialData(id: Id) {
    const payloadMap = new Map()
    const initialDataPayload = this.initialData

    if (initialDataPayload) {
      payloadMap.set(0, initialDataPayload?.proxyLinkCode)
      payloadMap.set(1, initialDataPayload?.currentVersion)
    }
    this.postMessage({
      id: id,
      type: AgentMessageTypes.GetInitialDataResponse,
      status: 0,
      statusMessage: 'Success',
      payload: payloadMap,
    })
  }

  private async eventListener(event: MessageEvent) {
    if (event.origin !== portalHost) return
    if (event.source !== this.appHost?.contentWindow) return

    const request = event.data as MeshAppMessage | undefined | null
    console.log('Received message from app:', request)

    if (!request || !this.meshAgent || !request.kind) return

    if (request.kind === requestMessageKind) {
      await this.handleRequestMessage(request as MeshAppRequestMessage)
    } else if (request.kind === navigationMessageKind) {
      this.handleNavigationMessage(request as MeshAppNavigationMessage)
    }
  }

  private handleNavigationMessage(message: MeshAppNavigationMessage) {
    const { url, isMeshAppInternalNavigation, newTab } = message
    if (!url) return
    const navigationUrl = isMeshAppInternalNavigation
      ? `${window.location.pathname}#${url}`
      : url

    if (newTab) {
      window.open(navigationUrl, '_blank')
    } else {
      window.location.href = navigationUrl
    }
  }

  private async handleRequestMessage(message: MeshAppRequestMessage) {
    const { type, id, payload } = message
    if (!type || !id || !this.meshAgent) return

    try {
      if (type === AgentMessageTypes.GetInitialDataRequest) {
        this.postInitialData(id)
        return
      }

      const cborResponse = await this.meshAgent.buildAndSendMessage({
        payload: payload ?? new Map(),
        messageType: type,
        messageId: id,
      })

      const headerCbor = cborResponse.get(0) as CborMap
      const responseHeader = deserialize(ResponseHeaderSchema, headerCbor)

      const payloadCbor = cborResponse.get(1) as CborMap | undefined

      this.postMessage({
        id: id,
        type: type,
        status: responseHeader.status,
        statusMessage: responseHeader.statusMessage,
        payload: payloadCbor instanceof Map ? payloadCbor : undefined,
      })
    } catch (err) {
      console.error('Error handling request message:', err)
      this.postMessage({
        id,
        type,
        status: AgentStatusCodes.CompanionError,
        statusMessage: 'Unknown error',
      })
    }
  }

  private postMessage(message: MeshAppRequestMessage) {
    this.appHost?.contentWindow?.postMessage(message, portalHost)
  }

  public enableSpaceNotifications() {
    if (!this.meshAgent) return
    this.meshAgent.addSpaceMessageNotificationListener((payload: CborMap) => {
      console.log('Space message notification received, forwarding to app')
      this.appHost?.contentWindow?.postMessage(
        {
          kind: notificationMessageKind,
          type: AgentMessageTypes.MASpaceMessageNotificationType,
          payload,
        },
        portalHost,
      )
    })
    console.log('Space notifications enabled')
  }

  public enableReconnectedNotifications() {
    if (!this.meshAgent) return
    this.meshAgent.setReconnectedListener(() => {
      console.log('WebSocket reconnected, forwarding notification to app')
      this.appHost?.contentWindow?.postMessage(
        {
          kind: notificationMessageKind,
          type: AgentMessageTypes.ReconnectedNotification,
        },
        portalHost,
      )
    })
  }

  public setInitialData(data?: LookupSiteResponsePayload) {
    this.initialData = data
  }

  public getInitialData(): LookupSiteResponsePayload | undefined {
    return this.initialData
  }

  dispose() {
    this.appHost = undefined
    this.meshAgent = undefined
    window.removeEventListener('message', this.boundHandleMessage)
  }
}
